import React, { createContext, ReactNode } from "react"
import { useLocation } from "react-router-dom"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import { Elements } from "@stripe/react-stripe-js"

import {
  useStripeWithRetry,
  UseStripeWithRetryResult,
} from "@fuse/customHooks/useStripeWithRetry"

interface StripeLoadingContextValue {
  isStripeLoaded: boolean
  stripeError: boolean
  retryStripe: () => void
  attempts: number
}

const StripeLoadingContext = createContext<StripeLoadingContextValue>({
  isStripeLoaded: false,
  stripeError: false,
  retryStripe: () => {},
  attempts: 0,
})

interface StripeProviderProps {
  children: ReactNode
  loadingComponent?: ReactNode
  errorComponent?: (error: Error, retry: () => void) => ReactNode
}

export const StripeProvider: React.FC<StripeProviderProps> = ({
  children,
  loadingComponent,
  errorComponent,
}) => {
  const { stripe, isLoading, error, isLoaded, retry, attempts } =
    useStripeWithRetry()
  const location = useLocation()

  // The checkout route pattern (see ROUTES.STRIPE_CHECKOUT)
  // /events/:eventId/eventSignup/stripeCheckout
  // We'll use a regex to match this pattern
  const isCheckoutPage = /^\/events\/[^/]+\/eventSignup\/stripeCheckout$/.test(
    location.pathname,
  )

  const contextValue: StripeLoadingContextValue = {
    isStripeLoaded: isLoaded,
    stripeError: !!error,
    retryStripe: retry,
    attempts,
  }

  const StripeErrorComponent = () => {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <BlurWrapper
          overlayOpacityDefault="20"
          className="rounded-2xl text-center max-w-sm md:max-w-xl w-full flex flex-col"
        >
          <div className="flex max-w-lg md:px-8 items-center justify-center self-center">
            <div className="px-6 py-10">
              <p className="text-7xl">⚠️</p>
              <p className="text-3xl mb-3 mt-6 text-white">
                Payment System Unavailable
              </p>
              <p
                className="mb-4 text-white/50 font-thin"
                style={{ whiteSpace: "pre-wrap" }}
              >
                We're having trouble loading the payment system. This might be
                due to a network issue.
              </p>

              <div className={`flex justify-center flex-col mt-8`}>
                <Button
                  size="lg"
                  className={"md:max-w-64 w-full min-w-48 mx-auto"}
                  onClick={() => {
                    retry()
                  }}
                >
                  <p className={"font-thin"}>Try Again</p>
                </Button>
              </div>
            </div>
          </div>
        </BlurWrapper>
      </div>
    )
  }

  // Only block user if on checkout page
  if (isCheckoutPage) {
    // Show loading state while Stripe is loading
    if (isLoading) {
      return (
        <StripeLoadingContext.Provider value={contextValue}>
          {loadingComponent || (
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-white">Loading payment system...</p>
              </div>
            </div>
          )}
        </StripeLoadingContext.Provider>
      )
    }

    // Show error state if Stripe failed to load
    if (error) {
      return (
        <StripeLoadingContext.Provider value={contextValue}>
          {errorComponent ? (
            errorComponent(error, retry)
          ) : (
            <div
              style={{
                background: `url(/assets/alpha-event-not-found.svg)`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              className="flex flex-col min-h-screen"
            >
              <div className="flex items-center justify-center min-h-screen">
                <StripeErrorComponent />
              </div>
            </div>
          )}
        </StripeLoadingContext.Provider>
      )
    }
  }

  // Stripe loaded successfully or not on checkout page - provide Elements context
  return (
    <StripeLoadingContext.Provider value={contextValue}>
      <Elements stripe={stripe}>{children}</Elements>
    </StripeLoadingContext.Provider>
  )
}

export { StripeLoadingContext }
